import { useEffect, useState } from 'react';
import { api } from '../lib/api';

interface FallbackStats {
  total: number; fallbacks: number; quotaFailures: number; providerFailures: number;
  avgAttempts: number; localRenders: number; reasons: { reason: string; count: number }[];
}

interface Breakdown {
  today: number; week: number; month: number;
  byType: { type: string; count: number; cost: number }[];
  byProvider: { providerKey: string; count: number; cost: number }[];
  byBrand: { brandId: string; count: number; cost: number }[];
}

interface ImageCosts {
  today: { cost: number; count: number }; week: { cost: number; count: number }; month: { cost: number; count: number };
  byProvider: { provider: string; count: number; cost: number }[];
  byModel: { provider: string; model: string; count: number; cost: number }[];
  byTier: { tier: string; count: number; cost: number }[];
  byContentType: { contentType: string; count: number; cost: number }[];
  premium: { count: number; cost: number };
  cache: { hits: number; savedUsd: number };
  budgets: { dailyUsd: number; premiumDailyUsd: number; spentTodayUsd: number; premiumTodayUsd: number };
}

const fmt = (n: number) => `$${n.toFixed(4)}`;

export function AiCostsPage() {
  const [bd, setBd] = useState<Breakdown | null>(null);
  const [gens, setGens] = useState<{ providerKey: string; modelId: string | null; type: string; estimatedCost: number | null; createdAt: string; status: string }[]>([]);
  const [type, setType] = useState('');
  const [provider, setProvider] = useState('');
  const [fb, setFb] = useState<(FallbackStats & { localSavingUsd?: number }) | null>(null);
  const [img, setImg] = useState<ImageCosts | null>(null);

  useEffect(() => {
    api.costSummary().catch(() => undefined);
    api.imageCosts().then((v) => setImg(v)).catch(() => undefined);
    api.generations(200).then((g) => setGens(g as never)).catch(() => undefined);
    void (async () => {
      const res = await fetch('/api/costs/ai');
      if (res.ok) setBd((await res.json()) as Breakdown);
      const fr = await fetch('/api/costs/fallback');
      if (fr.ok) setFb((await fr.json()) as FallbackStats);
    })();
  }, []);

  const byType = bd?.byType.filter((t) => !type || t.type === type) ?? [];
  const byProvider = (bd?.byProvider ?? []).filter((p) => !provider || p.providerKey === provider);
  const recent = (gens ?? []).filter((g) => (!type || g.type === type) && (!provider || g.providerKey === provider));

  return (
    <div>
      <h2>AI Costs</h2>
      {bd && (
        <div className="card">
          <div className="card__header">Summary</div>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div><strong>Today</strong><br /><span className="muted">{fmt(bd.today)}</span></div>
            <div><strong>Week</strong><br /><span className="muted">{fmt(bd.week)}</span></div>
            <div><strong>Month</strong><br /><span className="muted">{fmt(bd.month)}</span></div>
          </div>
        </div>
      )}

      {fb && (
        <div className="card">
          <div className="card__header">Fallback analytics</div>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div><strong>{fb.fallbacks}</strong><br /><span className="muted">fallbacks / {fb.total}</span></div>
            <div><strong>{fb.quotaFailures}</strong><br /><span className="muted">quota/rate failures</span></div>
            <div><strong>{fb.providerFailures}</strong><br /><span className="muted">provider failures</span></div>
            <div><strong>{fb.avgAttempts.toFixed(2)}</strong><br /><span className="muted">avg attempts</span></div>
            <div><strong>{fb.localRenders}</strong><br /><span className="muted">LOCAL renders</span></div>
            <div><strong>${(fb.localSavingUsd ?? 0).toFixed(3)}</strong><br /><span className="muted">~LOCAL cost avoided</span></div>
          </div>
          {fb.reasons.length > 0 && (
            <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
              reasons: {fb.reasons.map((r) => `${r.reason}×${r.count}`).join(', ')}
            </p>
          )}
        </div>
      )}

      {img && (
        <div className="card">
          <div className="card__header">Image costs (router)</div>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div><strong>{fmt(img.today.cost)}</strong><br /><span className="muted">24h · {img.today.count} img</span></div>
            <div><strong>{fmt(img.week.cost)}</strong><br /><span className="muted">7d · {img.week.count} img</span></div>
            <div><strong>{fmt(img.month.cost)}</strong><br /><span className="muted">30d · {img.month.count} img</span></div>
            <div><strong>{img.cache.hits}</strong><br /><span className="muted">cache hits</span></div>
            <div><strong>{fmt(img.cache.savedUsd)}</strong><br /><span className="muted">cache savings</span></div>
            <div><strong>{fmt(img.premium.cost)}</strong><br /><span className="muted">premium · {img.premium.count}</span></div>
          </div>
          {(img.budgets.dailyUsd > 0 || img.budgets.premiumDailyUsd > 0) && (
            <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
              daily {fmt(img.budgets.spentTodayUsd)}{img.budgets.dailyUsd > 0 ? ` / ${fmt(img.budgets.dailyUsd)}` : ''}
              {' · '}premium {fmt(img.budgets.premiumTodayUsd)}{img.budgets.premiumDailyUsd > 0 ? ` / ${fmt(img.budgets.premiumDailyUsd)}` : ''}
              {img.budgets.dailyUsd > 0 && img.budgets.spentTodayUsd >= img.budgets.dailyUsd ? ' · DAILY CAP — router degraded' : ''}
              {img.budgets.premiumDailyUsd > 0 && img.budgets.premiumTodayUsd >= img.budgets.premiumDailyUsd ? ' · PREMIUM CAP' : ''}
            </p>
          )}
          <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 280 }}>
              <div className="muted" style={{ fontSize: 12 }}>By model</div>
              <table>
                <thead><tr><th>Provider</th><th>Model</th><th>Count</th><th>Cost</th></tr></thead>
                <tbody>
                  {img.byModel.slice(0, 12).map((m, i) => (
                    <tr key={i}><td>{m.provider}</td><td>{m.model}</td><td>{m.count}</td><td>{fmt(m.cost)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ flex: 1, minWidth: 280 }}>
              <div className="muted" style={{ fontSize: 12 }}>By tier / content type</div>
              <table>
                <thead><tr><th>Kind</th><th>Count</th><th>Cost</th></tr></thead>
                <tbody>
                  {img.byTier.map((t, i) => (<tr key={`t${i}`}><td>{t.tier}</td><td>{t.count}</td><td>{fmt(t.cost)}</td></tr>))}
                  {img.byContentType.map((t, i) => (<tr key={`c${i}`}><td>{t.contentType}</td><td>{t.count}</td><td>{fmt(t.cost)}</td></tr>))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
        <div className="card" style={{ flex: 1 }}>
          <div className="card__header">
            By type
            <select value={type} onChange={(e) => setType(e.target.value)} style={{ marginLeft: 10 }}>
              <option value="">all</option>
              {bd?.byType.map((t) => <option key={t.type} value={t.type}>{t.type}</option>)}
            </select>
          </div>
          <table>
            <thead><tr><th>Type</th><th>Count</th><th>Cost</th></tr></thead>
            <tbody>
              {byType.map((t) => (
                <tr key={t.type}>
                  <td>{t.type}</td><td>{t.count}</td><td>{fmt(t.cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card" style={{ flex: 1 }}>
          <div className="card__header">
            By provider
            <select value={provider} onChange={(e) => setProvider(e.target.value)} style={{ marginLeft: 10 }}>
              <option value="">all</option>
              {bd?.byProvider.map((p) => <option key={p.providerKey} value={p.providerKey}>{p.providerKey}</option>)}
            </select>
          </div>
          <table>
            <thead><tr><th>Provider</th><th>Count</th><th>Cost</th></tr></thead>
            <tbody>
              {byProvider.map((p) => (
                <tr key={p.providerKey}>
                  <td>{p.providerKey}</td><td>{p.count}</td><td>{fmt(p.cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card__header">Recent generations ({recent.length})</div>
        <table>
          <thead><tr><th>When</th><th>Provider</th><th>Model</th><th>Type</th><th>Status</th><th>Cost</th></tr></thead>
          <tbody>
            {recent.slice(0, 50).map((g, i) => (
              <tr key={i}>
                <td className="muted" style={{ fontSize: 12 }}>{g.createdAt?.slice(0, 16)}</td>
                <td>{g.providerKey}</td><td>{g.modelId ?? '—'}</td><td>{g.type}</td>
                <td>{g.status}</td><td>{g.estimatedCost != null ? fmt(g.estimatedCost) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}